import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type RevealProps = {
  children: ReactNode;
  /** Direction the element travels in from. */
  from?: "left" | "right" | "up";
  /** Animation delay in seconds, mirroring the previous framer-motion delays. */
  delay?: number;
  className?: string;
};

const fromClass = {
  left: "reveal-from-left",
  right: "reveal-from-right",
  up: "reveal-from-up",
} as const;

/**
 * Scroll-triggered fade/slide, replacing framer-motion's `whileInView`.
 * Animates once and respects prefers-reduced-motion (handled in index.css).
 */
const Reveal = ({ children, from = "up", delay = 0, className }: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      // The root is extended far above the viewport so anything scrolled past
      // counts as intersecting. An anchor jump (e.g. "/#booking") can move an
      // element from below the viewport to above it in one frame; without this
      // the ratio never leaves 0, no callback fires, and it stays invisible.
      // The bottom is still pulled in so elements reveal just before entering.
      { rootMargin: "100000px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn("reveal", fromClass[from], shown && "reveal-shown", className)}
      style={delay ? { transitionDelay: `${delay}s` } : undefined}
    >
      {children}
    </div>
  );
};

export default Reveal;
